import { useFetchDataSources } from '@highimpact/source-selector';
import { useEffect, useMemo, useState } from 'react';

import { usePermissionFetch } from '@highimpact/dashboard-ui';
import { buildWidgetDataSourceRequest } from './buildWidgetDataSourceRequest';
import type { BuildWidgetDataSourceRequestOptions } from './types';

// -- type definitions --

export interface UseWidgetSourceSelectionOptions
  extends BuildWidgetDataSourceRequestOptions {
  readonly tokenKey?: string;
  readonly url?: string;
}

// -- main --

/**
 * Fetches scoped data-source candidates for a built-in widget configuration form.
 *
 * Consults the `request_sources_for_whole_project` frontend permission
 * to check if project-wide requests are permitted.
 *
 * @param options - Widget kind, dashboard scope, and optional fetch overrides.
 * @returns The resolved request, fetch state, and catalog payload for selectors.
 */
export function useWidgetSourceSelection({
  widget,
  scope,
  independentDimensionKeys,
  tokenKey,
  url,
}: UseWidgetSourceSelectionOptions) {
  const fetchPermission = usePermissionFetch();

  const [wholeProjectPermission, setWholeProjectPermission] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    const abortController = new AbortController();
    if (!fetchPermission) {
      return;
    }

    fetchPermission(
      {
        item: 'request_sources_for_whole_project',
        ...scope,
      },
      abortController.signal
    ).then((permission) => {
      if (permission.action === 'display') {
        setWholeProjectPermission(true);
      } else {
        setWholeProjectPermission(false);
      }
    });

    return () => {
      abortController.abort();
    };
  }, [fetchPermission, scope]);

  const request = useMemo(
    () =>
      buildWidgetDataSourceRequest({
        widget,
        scope: wholeProjectPermission
          ? { project_id: scope.project_id, site_id: null }
          : scope,
        independentDimensionKeys,
      }),
    [widget, scope, independentDimensionKeys, wholeProjectPermission]
  );

  const fetchResult = useFetchDataSources({
    request,
    tokenKey,
    url,
  });

  return {
    request,
    ...fetchResult,
  };
}
